import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  Container,
  Paper,
  Box,
  Stepper,
  Step,
  StepLabel,
  Typography,
  TextField,
  Button,
  Stack,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Divider,
  Alert,
  CircularProgress,
  LinearProgress,
  InputAdornment,
  useMediaQuery,
  useTheme
} from '@mui/material';
import axios from '../utils/axios';
import * as FallRetreatForm from './campusMinistry/FallRetreatForm';
import {
  STUDENT_CATEGORY_LABELS,
  priceForCategory,
  formatCurrency,
  attachSquareCard,
  newIdempotencyKey
} from '../utils/campusMinistryFields';

// Each Campus Ministry form type plugs its steps into this shell. The shell
// owns loading the current event, the header/stepper, payment and submit.
const FORMS = {
  'fall-retreat': FallRetreatForm
};

const CARD_CONTAINER_ID = 'cm-reg-card-container';

const CampusMinistryRegistration = () => {
  const { formType } = useParams();
  const Form = FORMS[formType];
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [event, setEvent] = useState(null);
  const [loadingEvent, setLoadingEvent] = useState(true);
  const [activeStep, setActiveStep] = useState(0);
  const [form, setForm] = useState(() => (Form ? Form.initialForm() : {}));
  const [paymentChoice, setPaymentChoice] = useState(''); // 'online' | 'in-person' | 'hardship'
  const [onlineAmount, setOnlineAmount] = useState('');
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const [squareReady, setSquareReady] = useState(false);
  const [squareCard, setSquareCard] = useState(null);
  // One payment idempotency key per logical submission: kept across retries so
  // a resubmit after an ambiguous failure can never charge the card twice.
  const paymentIdemKeyRef = useRef(null);

  const steps = Form ? [...Form.steps, 'Payment'] : [];
  const PAYMENT_STEP = steps.length - 1;
  const amountDue = priceForCategory(event, form.studentCategory);
  const minimumOnline = Math.min(Number(event?.minimumOnlinePayment) || 1, amountDue);

  useEffect(() => {
    if (!Form) {
      setLoadingEvent(false);
      return;
    }
    const load = async () => {
      try {
        const res = await axios.get(`/campus-ministry/events/public/${formType}`);
        setEvent(res.data);
      } catch (e) {
        console.error('Failed to load Campus Ministry event:', e);
        setEvent({ registrationEnabled: false });
      } finally {
        setLoadingEvent(false);
      }
    };
    load();
  }, [formType]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (event?.name) document.title = `${event.name} Registration`;
  }, [event]);

  // Initialize Square when on the payment step paying online with a charge due.
  useEffect(() => {
    if (activeStep !== PAYMENT_STEP) return;
    if (paymentChoice !== 'online') return;
    if (amountDue < 1) return;
    if (squareReady) return;

    let cancelled = false;
    const init = async () => {
      try {
        const card = await attachSquareCard(axios, CARD_CONTAINER_ID);
        if (cancelled) { try { await card.destroy(); } catch (_) {} return; }
        setSquareCard(card);
        setSquareReady(true);
      } catch (e) {
        console.error('Square init failed:', e);
        setError('The payment system could not be loaded. Please refresh the page, or choose "Pay in person".');
      }
    };
    init();
    return () => { cancelled = true; };
  }, [activeStep, paymentChoice, amountDue, squareReady, PAYMENT_STEP]);

  // Leaving the payment step unmounts the card container; drop the card so it
  // is re-attached on return.
  useEffect(() => {
    if (activeStep === PAYMENT_STEP || !squareCard) return;
    try { squareCard.destroy(); } catch (_) {}
    setSquareCard(null);
    setSquareReady(false);
  }, [activeStep, PAYMENT_STEP, squareCard]);

  const setField = (path, value) => {
    setForm(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      const keys = path.split('.');
      let cur = next;
      keys.forEach((k, i) => {
        if (i === keys.length - 1) cur[k] = value;
        else { if (!cur[k] || typeof cur[k] !== 'object') cur[k] = {}; cur = cur[k]; }
      });
      return next;
    });
  };

  const handleNext = () => {
    const message = Form.validateStep(activeStep, form);
    if (message) {
      setError(message);
      return;
    }
    setError(null);
    // Default the online amount to the full price (category may have changed).
    if (activeStep === PAYMENT_STEP - 1) {
      setOnlineAmount(amountDue.toFixed(2));
    }
    setActiveStep(s => Math.min(s + 1, PAYMENT_STEP));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const handleBack = () => {
    setError(null);
    setActiveStep(s => Math.max(s - 1, 0));
  };

  const handleSubmit = async () => {
    if (amountDue > 0 && !paymentChoice) {
      setError('Please choose how you would like to pay.');
      return;
    }
    const choice = amountDue > 0 ? paymentChoice : 'in-person';
    const amount = Math.round(parseFloat(onlineAmount) * 100) / 100;
    if (choice === 'online' && (!amount || amount < minimumOnline || amount > amountDue)) {
      setError(`Please enter an amount between ${formatCurrency(minimumOnline)} and ${formatCurrency(amountDue)}.`);
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        ...Form.toPayload(form),
        formType,
        paymentChoice: choice
      };

      if (choice === 'online') {
        if (!squareCard) {
          setError('The payment form is not ready yet. Please wait a moment and try again.');
          setSubmitting(false);
          return;
        }
        const tokenResult = await squareCard.tokenize();
        if (tokenResult.status !== 'OK') {
          const msg = (tokenResult.errors || []).map(e => e.message).join(', ') || 'Please check your card details.';
          throw new Error(msg);
        }
        payload.squarePaymentToken = tokenResult.token;
        payload.paymentAmount = amount;
        if (!paymentIdemKeyRef.current) paymentIdemKeyRef.current = newIdempotencyKey();
        payload.paymentIdempotencyKey = paymentIdemKeyRef.current;
      }

      const res = await axios.post('/campus-ministry/public-submission', payload);
      setResult({ ...(res.data || {}), choice, amount: choice === 'online' ? amount : 0 });
      window.scrollTo({ top: 0 });
    } catch (e) {
      // 400/402/403 mean the server rejected before charging, so a retry is a
      // new payment attempt and needs a fresh key. Any other failure is
      // ambiguous — keep the key so a resubmit cannot double-charge.
      const status = e?.response?.status;
      if (status === 400 || status === 402 || status === 403) {
        paymentIdemKeyRef.current = null;
      }
      setError(e?.response?.data?.message || e?.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Dev/test-only filler. Shown when ?test=1 is on the URL or running on localhost.
  const showTestData = (() => {
    try {
      const qp = new URLSearchParams(window.location.search).get('test');
      const host = window.location.hostname;
      return qp === '1' || host === 'localhost' || host === '127.0.0.1';
    } catch { return false; }
  })();

  if (loadingEvent) {
    return (
      <Container maxWidth="sm" sx={{ py: 6, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!Form || !event || event.registrationEnabled === false) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Paper sx={{ p: 4 }}>
          <Typography variant="h5" gutterBottom>Registration is closed</Typography>
          <Typography color="text.secondary">
            {Form
              ? 'Registration for this event is not open right now. Please check back later or contact the parish office at 856-881-2753.'
              : 'This registration form could not be found.'}
          </Typography>
        </Paper>
      </Container>
    );
  }

  if (result) {
    const paidNow = result.amount || 0;
    const remaining = Math.max(0, Math.round((amountDue - paidNow) * 100) / 100);
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Paper sx={{ p: 4 }}>
          <Typography variant="h5" gutterBottom color="success.main">You're registered!</Typography>
          <Typography sx={{ mb: 2 }}>
            {result.pendingManualReview
              ? result.message
              : `Thank you for signing up for the ${event.name}. A confirmation email is on its way to ${form.email.trim()}.`}
          </Typography>
          {result.choice === 'hardship' && (
            <Alert severity="info">Someone from Campus Ministry will reach out about a sponsor. No one is held back because of finances.</Alert>
          )}
          {result.choice !== 'hardship' && remaining > 0 && !result.pendingManualReview && (
            <Alert severity="info">
              {paidNow > 0 ? `You paid ${formatCurrency(paidNow)}. ` : ''}
              Remaining balance: {formatCurrency(remaining)}. You can pay in person, or online any time at{' '}
              <a href={`/campus-ministry/${formType}/payment`}>the payment page</a>.
            </Alert>
          )}
          {result.choice === 'online' && remaining <= 0 && !result.pendingManualReview && (
            <Alert severity="success">You are paid in full.</Alert>
          )}
        </Paper>
      </Container>
    );
  }

  const renderPaymentStep = () => (
    <Box>
      <Typography variant="h6" gutterBottom>Payment</Typography>
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" justifyContent="space-between">
          <Typography>{STUDENT_CATEGORY_LABELS[form.studentCategory] || 'Registration'}</Typography>
          <Typography>{formatCurrency(amountDue)}</Typography>
        </Stack>
        <Divider sx={{ my: 1 }} />
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Amount Due</Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{formatCurrency(amountDue)}</Typography>
        </Stack>
      </Paper>

      {event.hardshipMessage && (
        <Alert severity="info" sx={{ mb: 2 }}>{event.hardshipMessage}</Alert>
      )}

      {amountDue < 1 ? (
        <Alert severity="info" sx={{ mb: 2 }}>There is no fee due. Click Submit to finish your registration.</Alert>
      ) : (
        <FormControl sx={{ mb: 2 }}>
          <FormLabel>How would you like to pay?</FormLabel>
          <RadioGroup value={paymentChoice} onChange={(e) => setPaymentChoice(e.target.value)}>
            <FormControlLabel value="online" control={<Radio />} label="Pay online now (any amount — pay the rest later)" />
            <FormControlLabel value="in-person" control={<Radio />} label="Pay in person" />
            <FormControlLabel value="hardship" control={<Radio />} label="Money is an issue — I'd like help with the cost" />
          </RadioGroup>
        </FormControl>
      )}

      {/* Kept mounted while on this step so an attached Square card survives
          switching payment options; hidden unless paying online. */}
      {amountDue >= 1 && (
        <Box sx={{ mb: 2, display: paymentChoice === 'online' ? 'block' : 'none' }}>
          <TextField
            label="Amount to pay now"
            type="number"
            value={onlineAmount}
            onChange={(e) => setOnlineAmount(e.target.value)}
            inputProps={{ min: minimumOnline, max: amountDue, step: '0.01' }}
            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
            helperText={`Between ${formatCurrency(minimumOnline)} and ${formatCurrency(amountDue)}`}
            sx={{ mb: 2, maxWidth: 260 }}
            fullWidth
          />
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Card Information</Typography>
          <Box id={CARD_CONTAINER_ID} sx={{ p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, minHeight: 60 }} />
          {!squareReady && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">Loading secure payment form…</Typography>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );

  const submitDisabled = submitting || (amountDue >= 1 && !paymentChoice);

  return (
    <Box sx={{ bgcolor: '#eef2f6', minHeight: '100vh', py: { xs: 2, md: 5 } }}>
      <Container maxWidth="md" sx={{ px: { xs: 1.5, sm: 2 } }}>
        {/* Header banner */}
        <Paper elevation={3} sx={{ borderRadius: 3, overflow: 'hidden', mb: 3 }}>
          <Box sx={{ background: 'linear-gradient(135deg, #1565c0 0%, #42a5f5 100%)', color: '#fff', px: { xs: 3, md: 5 }, py: { xs: 3, md: 4 } }}>
            <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ fontWeight: 700 }}>{event.name}</Typography>
            <Typography variant="subtitle1" sx={{ opacity: 0.92, mt: 0.5 }}>{event.organizationName} · {event.parishName}</Typography>
            {event.schedule?.dates && (
              <Typography variant="body2" sx={{ opacity: 0.92, mt: 1 }}>
                {event.schedule.dates}{event.schedule.optionalDates ? ` · Optional: ${event.schedule.optionalDates}` : ''}
              </Typography>
            )}
          </Box>
        </Paper>

        {/* Progress */}
        <Paper elevation={1} sx={{ borderRadius: 3, p: { xs: 2, md: 2.5 }, mb: 3 }}>
          {!isMobile ? (
            <Stepper activeStep={activeStep} alternativeLabel>
              {steps.map((label) => (
                <Step key={label}><StepLabel>{label}</StepLabel></Step>
              ))}
            </Stepper>
          ) : (
            <Box>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 700 }}>{steps[activeStep]}</Typography>
                <Typography variant="caption" color="text.secondary">{activeStep + 1} / {steps.length}</Typography>
              </Stack>
              <LinearProgress variant="determinate" value={((activeStep + 1) / steps.length) * 100} sx={{ height: 6, borderRadius: 3 }} />
            </Box>
          )}
        </Paper>

        {/* Step content */}
        <Paper elevation={2} sx={{ borderRadius: 3, p: { xs: 2.5, md: 4 } }}>
          {showTestData && (
            <Box sx={{ mb: 2, textAlign: 'right' }}>
              <Button size="small" variant="outlined" color="secondary" onClick={() => { setForm(Form.testData()); setPaymentChoice('in-person'); setError(null); }}>
                Fill Test Data
              </Button>
            </Box>
          )}
          {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}
          {activeStep < PAYMENT_STEP
            ? Form.renderStep({ step: activeStep, form, setField, setForm, event })
            : renderPaymentStep()}
          <Divider sx={{ my: 3 }} />
          <Stack direction="row" justifyContent="space-between">
            <Button disabled={activeStep === 0 || submitting} onClick={handleBack} size="large">Back</Button>
            {activeStep < PAYMENT_STEP ? (
              <Button variant="contained" size="large" onClick={handleNext}>Next</Button>
            ) : (
              <Button variant="contained" color="success" size="large" onClick={handleSubmit} disabled={submitDisabled}>
                {submitting ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Submit'}
              </Button>
            )}
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
};

export default CampusMinistryRegistration;
